import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
	DocumentNumberCheck,
	DriverLedgerCategoryOption,
	DriverLedgerCategoryOptionApiData,
	DriverLedgerEntry,
	DriverLedgerEntryApiData,
	DriverLedgerFilters,
	DriverLedgerPaginatedApiData,
	DriverLedgerPaginatedResponse,
	DriverLedgerPendingSummary,
	DriverLedgerPendingSummaryApiData,
	DriverLedgerSummary,
	DriverLedgerSummaryApiData,
	StoreDriverLedgerPayload,
	UpdateDriverLedgerPayload,
	VehicleSearchResult,
} from '../_models/driver-ledger.model';
import { snakeToCamelCaseObject } from '@/_utils/string/snake-to-camel-case';
import { buildHttpParams } from '@/common/utils/http-params.util';

@Injectable({
	providedIn: 'root',
})
export class DriverLedgerApiService {
	private readonly httpClient = inject(HttpClient);
	private readonly apiUrl = environment.erpSymfonyUrl;

	getLedger(driverId: string, filters?: DriverLedgerFilters): Observable<DriverLedgerPaginatedResponse> {
		const params = buildHttpParams({
			page: filters?.page,
			perPage: filters?.perPage,
			date_from: filters?.dateFrom,
			date_to: filters?.dateTo,
			currency: filters?.currency,
			category_id: filters?.categoryId,
			status: filters?.status,
			settled_by_salary_id: filters?.settledBySalaryId,
		});

		return this.httpClient
			.get<DriverLedgerPaginatedApiData>(`${this.apiUrl}/driver/${driverId}/ledger`, { params })
			.pipe(
				map((response) => ({
					items: snakeToCamelCaseObject(response.data.items),
					total: response.data.total,
					page: response.data.page,
					perPage: response.data.per_page,
					totalPages: response.data.total_pages,
				})),
			);
	}

	getPendingSummary(driverId: string, settledBySalaryId?: number): Observable<DriverLedgerPendingSummary> {
		const params = buildHttpParams({ settled_by_salary_id: settledBySalaryId });

		return this.httpClient
			.get<DriverLedgerPendingSummaryApiData>(`${this.apiUrl}/driver/${driverId}/ledger/pending-summary`, {
				params,
			})
			.pipe(
				map((response) => ({
					count: response.data?.count ?? 0,
					totalPln: response.data?.total_pln ?? 0,
					totalEur: response.data?.total_eur ?? 0,
					totalEurPln: response.data?.total_eur_pln ?? 0,
				})),
			);
	}

	getSummary(driverId: string): Observable<DriverLedgerSummary> {
		return this.httpClient.get<DriverLedgerSummaryApiData>(`${this.apiUrl}/driver/${driverId}/ledger/summary`).pipe(
			map((response) => ({
				currencies: (response.data?.currencies ?? []).map((c) => ({
					currency: c.currency,
					balance: c.balance,
					balancePln: c.balance_pln,
					balanceEur: c.balance_eur,
				})),
				totalPln: response.data?.total_pln ?? 0,
				totalEur: response.data?.total_eur ?? 0,
			})),
		);
	}

	createEntry(driverId: string, payload: StoreDriverLedgerPayload): Observable<DriverLedgerEntry> {
		return this.httpClient
			.post<{ data: DriverLedgerEntryApiData }>(`${this.apiUrl}/driver/${driverId}/ledger`, payload)
			.pipe(map((response) => snakeToCamelCaseObject(response.data)));
	}

	updateEntry(driverId: string, entryId: number, payload: UpdateDriverLedgerPayload): Observable<DriverLedgerEntry> {
		return this.httpClient
			.put<{ data: DriverLedgerEntryApiData }>(`${this.apiUrl}/driver/${driverId}/ledger/${entryId}`, payload)
			.pipe(map((response) => snakeToCamelCaseObject(response.data)));
	}

	toggleOriginalDocument(
		driverId: string,
		entryId: number,
		hasOriginalDocument: boolean,
	): Observable<DriverLedgerEntry> {
		return this.httpClient
			.patch<{
				data: DriverLedgerEntryApiData;
			}>(`${this.apiUrl}/driver/${driverId}/ledger/${entryId}/original-document`, {
				has_original_document: hasOriginalDocument,
			})
			.pipe(map((response) => snakeToCamelCaseObject(response.data)));
	}

	deleteEntry(driverId: string, entryId: number): Observable<void> {
		return this.httpClient.delete<void>(`${this.apiUrl}/driver/${driverId}/ledger/${entryId}`);
	}

	checkDocumentNumber(driverId: string, documentNumber: string): Observable<DocumentNumberCheck> {
		return this.httpClient
			.get<{
				data: DocumentNumberCheck;
			}>(`${this.apiUrl}/driver/${driverId}/ledger/document-number-check`, {
				params: buildHttpParams({ documentNumber }),
			})
			.pipe(map((response) => response.data));
	}

	getCategories(): Observable<DriverLedgerCategoryOption[]> {
		return this.httpClient
			.get<{ data: DriverLedgerCategoryOptionApiData[] }>(`${this.apiUrl}/driver-ledger/categories`)
			.pipe(map((r) => snakeToCamelCaseObject(r.data)));
	}

	searchVehicles(query: string): Observable<VehicleSearchResult[]> {
		return this.httpClient.get<VehicleSearchResult[]>(`${this.apiUrl}/vehicles/search`, {
			params: buildHttpParams({ by: query }),
		});
	}
}
